'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { Input } from '@brew/ui/input'
import { useTranslations } from 'next-intl'
import { type ReactNode, useCallback, useMemo, useState } from 'react'
import {
  ROUNDED_SEARCH_INPUT_CLASSNAME,
  RoundedSearchBar,
} from '@/components/audience/rounded-search-bar'
import { WordRotate } from '@/components/magicui/word-rotate'
import { MARKETING_CANVAS_BG } from '@/components/marketing/v2/marketing-layout'
import { useFlowSearchQuery } from '@/lib/flow-templates/search-query-state'
import { useTemplatesSurface } from '../lib/templates-surface-context'

/** Brands the placeholder suggests, before its closing example. */
const BRAND_EXAMPLES = ['Notion…', 'Atlassian…', 'Substack…', 'Ramp…']

const PLACEHOLDER_TEXT = 'font-medium text-muted-foreground text-sm'

/** The placeholder's words before the rotating example. */
function placeholderLead(chunks: ReactNode) {
  return <p className={PLACEHOLDER_TEXT}>{chunks}</p>
}

/**
 * The flows gallery's search box: an instant match on the brand over the
 * flows the page already holds. Every keystroke is the query — the grid
 * filters as you type through the shared `q` state — and the URL follows
 * after a short debounce, so a search survives a reload and a share.
 * Enter has nothing left to do; Clear empties the query.
 */
export function FlowsSearchBar() {
  const t = useTranslations('flows')
  const isPublicSurface = useTemplatesSurface() === 'public'
  const [query, setQuery] = useFlowSearchQuery()
  const [isFocused, setIsFocused] = useState(false)
  const examples = useMemo(
    () => [...BRAND_EXAMPLES, t('search.admiredBrand')],
    [t]
  )

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
    },
    []
  )
  const handleClear = useCallback(() => {
    void setQuery('')
  }, [setQuery])
  const placeholderExample = useCallback(
    () => (
      <WordRotate
        words={examples}
        duration={4000}
        className={PLACEHOLDER_TEXT}
      />
    ),
    [examples]
  )

  const shouldShowPlaceholder = !isFocused && !query

  return (
    <RoundedSearchBar
      onSubmit={handleSubmit}
      className={cn('h-11', { [MARKETING_CANVAS_BG]: isPublicSurface })}
    >
      <Input
        type="search"
        value={query}
        placeholder=""
        aria-label={t('search.label')}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(event) => void setQuery(event.currentTarget.value)}
        className={cn(ROUNDED_SEARCH_INPUT_CLASSNAME, 'h-full text-sm', {
          'bg-transparent': isPublicSurface,
        })}
      />
      {shouldShowPlaceholder ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1 left-4 mt-2 flex select-none items-center gap-1"
        >
          {t.rich('search.placeholder', {
            lead: placeholderLead,
            example: placeholderExample,
          })}
        </div>
      ) : null}
      {query ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={t('search.clearSearch')}
          className="shrink-0 rounded-full text-muted-foreground"
          onClick={handleClear}
        >
          {t('search.clear')}
        </Button>
      ) : null}
    </RoundedSearchBar>
  )
}
